/**
 * Single source of truth for contextual internal-link sections
 * (related services, cost ↔ service pairs, breadcrumbs, blog → service CTAs).
 *
 * Edit this file to retune the site's internal linking — no component changes
 * required.
 */

export type LinkItem = {
  title: string;
  href: string;
  blurb?: string;
};

/** Display labels used by breadcrumbs and link cards, keyed by full path. */
export const PATH_LABELS: Record<string, string> = {
  "/": "Home",
  "/about-us": "About Us",
  "/contact-us": "Contact",
  "/blog": "Blog",
  "/free-roof-inspection-phoenix": "Free Roof Inspection",
  "/service-areas": "Service Areas",
  "/rosie-on-the-house": "Rosie on the House Partner",




  // Services
  "/foam-roofing": "Foam Roofing Installation",
  "/foam-roof-coating-recoating": "Foam Roof Coating & Recoating",
  "/foam-roof-repair-phoenix": "Foam Roof Repair",
  "/tile-roofing": "Tile Roofing",
  "/tile-roof-repair-phoenix": "Tile Roof Repair",
  "/shingle-roofing": "Shingle Roofing",
  "/roof-replacement-phoenix": "Roof Replacement",
  "/roof-repair": "Roof Repair",
  "/storm-damage-repair": "Storm & Hail Damage Repair",
  "/roof-leak-repair": "Roof Leak Repair",
  "/flat-roof-repair": "Flat Roof Repair",
  "/emergency-roof-leak-repair": "Emergency Roof Leak Repair",
  "/commercial-roofing": "Commercial Roofing",
  "/commercial-foam-roofing": "Commercial Foam Roofing",
  "/commercial-roof-repair": "Commercial Roof Repair",
  "/commercial-roof-coating": "Commercial Roof Coating",
  "/warehouse-roofing": "Warehouse Roofing",
  "/hoa-roofing": "HOA Roofing",
  "/apartment-roofing": "Apartment & Condo Roofing",
  "/skylight-repair": "Skylight Repair",
  "/skylight-replacement-phoenix": "Skylight Replacement",
  "/roof-maintenance": "Roof Maintenance",
  "/roof-inspections-for-realtors": "Roof Inspections for Realtors",

  // Costs
  "/roofing-costs-phoenix": "Phoenix Roofing Costs",
  "/foam-roof-recoating-cost-in-phoenix": "Foam Roof Recoating Cost",
  "/foam-roof-replacement-cost-phoenix": "Foam Roof Replacement Cost",
  "/tile-roof-replacement-cost-in-phoenix": "Tile Roof R&R Cost",
  "/shingle-roof-cost-phoenix": "Shingle Roof Cost",
  "/roof-repair-cost-phoenix": "Roof Repair Cost",
  "/flat-roof-repair-cost-phoenix": "Flat Roof Repair Cost",
  "/commercial-roofing-cost-phoenix": "Commercial Roofing Cost",
  "/roof-inspection-cost-phoenix": "Roof Inspection Cost",
  "/skylight-repair-cost-phoenix": "Skylight Repair Cost",
};

/** Short marketing blurb per page, used by related-link cards. */
export const PATH_BLURBS: Record<string, string> = {
  "/foam-roofing": "New SPF foam roofing built for Phoenix sun.",
  "/foam-roof-coating-recoating": "Reset your foam roof with a fresh elastomeric coating.",
  "/foam-roof-repair-phoenix": "Patch bubbles, blisters, and bird damage.",
  "/tile-roofing": "Underlayment replacement and tile reset.",
  "/tile-roof-repair-phoenix": "Cracked tile, valley leaks, and underlayment repair.",
  "/shingle-roofing": "Shingle repair and replacement for AZ heat.",
  "/roof-repair": "Targeted repair for flashings and storm damage.",
  "/storm-damage-repair": "Hail and monsoon damage documented for your insurance claim.",
  "/roof-leak-repair": "Find and stop active leaks — usually same day.",
  "/flat-roof-repair": "Repair and recoating for residential flat roofs.",
  "/emergency-roof-leak-repair": "Active leak right now? Call us first.",
  "/commercial-roofing": "Foam and single-ply systems for commercial buildings.",
  "/commercial-foam-roofing": "SPF foam installs & foam-over reroofs for commercial buildings.",
  "/commercial-roof-repair": "Same-week commercial repair on every major system.",
  "/commercial-roof-coating": "Elastomeric & silicone recoats — 10–15 years of new life.",
  "/warehouse-roofing": "Foam-first roofing for warehouses and distribution centers.",
  "/hoa-roofing": "Multi-unit foam recoats and HOA-friendly scheduling.",
  "/apartment-roofing": "Foam recoats and repairs for apartments and condos.",
  "/skylight-repair": "Skylight leak sealing, flashing, and replacement.",
  "/skylight-replacement-phoenix": "New skylights, solar units, and curb flashing done right.",
  "/roof-maintenance": "Scheduled inspections that extend roof life by years.",
  "/roof-inspections-for-realtors": "Pre-listing roof reports turned in fast.",

  "/foam-roof-recoating-cost-in-phoenix": "What a foam recoat actually costs in Phoenix.",
  "/foam-roof-replacement-cost-phoenix": "Real numbers for full foam roof replacement.",
  "/tile-roof-replacement-cost-in-phoenix": "Tile R&R cost ranges by roof size.",
  "/shingle-roof-cost-phoenix": "Shingle replacement cost in Phoenix.",
  "/roof-repair-cost-phoenix": "Typical repair pricing by issue type.",
  "/flat-roof-repair-cost-phoenix": "Flat roof repair cost breakdown.",
  "/commercial-roofing-cost-phoenix": "Commercial roofing cost by system type.",
  "/roof-inspection-cost-phoenix": "Why we keep inspections free.",
  "/skylight-repair-cost-phoenix": "Skylight repair vs replacement pricing.",
  "/roofing-costs-phoenix": "Every Phoenix roofing cost guide in one place.",

  "/free-roof-inspection-phoenix": "Free written photo inspection — no obligation.",
  "/rosie-on-the-house": "Rosie on the House Certified Partner since 1996.",
};

/** Service slug → its matching cost-page path (if any). */
export const SERVICE_TO_COST: Record<string, string> = {
  "foam-roofing": "/foam-roof-replacement-cost-phoenix",
  "foam-roof-coating-recoating": "/foam-roof-recoating-cost-in-phoenix",
  "foam-roof-repair-phoenix": "/roof-repair-cost-phoenix",
  "tile-roofing": "/tile-roof-replacement-cost-in-phoenix",
  "tile-roof-repair-phoenix": "/tile-roof-replacement-cost-in-phoenix",
  "shingle-roofing": "/shingle-roof-cost-phoenix",
  "roof-repair": "/roof-repair-cost-phoenix",
  "storm-damage-repair": "/roof-repair-cost-phoenix",
  "roof-leak-repair": "/roof-repair-cost-phoenix",
  "flat-roof-repair": "/flat-roof-repair-cost-phoenix",
  "commercial-roofing": "/commercial-roofing-cost-phoenix",
  "commercial-foam-roofing": "/commercial-roofing-cost-phoenix",
  "commercial-roof-repair": "/commercial-roofing-cost-phoenix",
  "commercial-roof-coating": "/commercial-roofing-cost-phoenix",
  "warehouse-roofing": "/commercial-roofing-cost-phoenix",
  "skylight-repair": "/skylight-repair-cost-phoenix",
  "skylight-replacement-phoenix": "/skylight-repair-cost-phoenix",
  "roof-inspections-for-realtors": "/roof-inspection-cost-phoenix",
  "roof-replacement-phoenix": "/roofing-costs-phoenix",
};

/** Cost slug → its matching service-page path(s). */
export const COST_TO_SERVICES: Record<string, string[]> = {
  "foam-roof-recoating-cost-in-phoenix": ["/foam-roof-coating-recoating", "/foam-roofing"],
  "foam-roof-replacement-cost-phoenix": ["/foam-roofing", "/foam-roof-coating-recoating"],
  "tile-roof-replacement-cost-in-phoenix": ["/tile-roofing", "/tile-roof-repair-phoenix", "/roof-repair"],
  "shingle-roof-cost-phoenix": ["/shingle-roofing", "/roof-repair"],
  "roof-repair-cost-phoenix": ["/roof-repair", "/roof-leak-repair", "/storm-damage-repair"],
  "flat-roof-repair-cost-phoenix": ["/flat-roof-repair", "/foam-roof-repair-phoenix"],
  "commercial-roofing-cost-phoenix": ["/commercial-roofing", "/commercial-foam-roofing", "/foam-roof-coating-recoating"],
  "roof-inspection-cost-phoenix": ["/free-roof-inspection-phoenix", "/roof-maintenance"],
  "skylight-repair-cost-phoenix": ["/skylight-repair", "/skylight-replacement-phoenix", "/roof-leak-repair"],
};

/** Service slug → 3 sibling-service paths to surface in a Related rail. */
export const RELATED_SERVICES: Record<string, string[]> = {
  "foam-roofing": ["/foam-roof-coating-recoating", "/foam-roof-repair-phoenix", "/roof-leak-repair"],
  "foam-roof-coating-recoating": ["/foam-roofing", "/foam-roof-repair-phoenix", "/roof-maintenance"],
  "foam-roof-repair-phoenix": ["/foam-roof-coating-recoating", "/roof-leak-repair", "/emergency-roof-leak-repair"],
  "tile-roofing": ["/tile-roof-repair-phoenix", "/roof-leak-repair", "/shingle-roofing"],
  "tile-roof-repair-phoenix": ["/tile-roofing", "/roof-leak-repair", "/roof-repair"],
  "shingle-roofing": ["/tile-roofing", "/roof-repair", "/roof-leak-repair"],
  "roof-repair": ["/roof-leak-repair", "/storm-damage-repair", "/tile-roof-repair-phoenix"],
  "storm-damage-repair": ["/roof-repair", "/emergency-roof-leak-repair", "/roof-leak-repair"],
  "roof-leak-repair": ["/emergency-roof-leak-repair", "/roof-repair", "/foam-roof-repair-phoenix"],
  "flat-roof-repair": ["/foam-roof-coating-recoating", "/foam-roof-repair-phoenix", "/roof-leak-repair"],
  "emergency-roof-leak-repair": ["/roof-leak-repair", "/roof-repair", "/foam-roof-repair-phoenix"],
  "commercial-roofing": ["/commercial-foam-roofing", "/commercial-roof-repair", "/commercial-roof-coating"],
  "commercial-foam-roofing": ["/commercial-roofing", "/commercial-roof-coating", "/warehouse-roofing"],
  "commercial-roof-repair": ["/commercial-roofing", "/commercial-roof-coating", "/emergency-roof-leak-repair"],
  "commercial-roof-coating": ["/commercial-foam-roofing", "/foam-roof-coating-recoating", "/commercial-roofing"],
  "warehouse-roofing": ["/commercial-foam-roofing", "/commercial-roofing", "/commercial-roof-coating"],
  "hoa-roofing": ["/commercial-roofing", "/apartment-roofing", "/commercial-foam-roofing"],
  "apartment-roofing": ["/commercial-foam-roofing", "/commercial-roofing", "/hoa-roofing"],
  "skylight-repair": ["/skylight-replacement-phoenix", "/roof-leak-repair", "/roof-repair"],
  "skylight-replacement-phoenix": ["/skylight-repair", "/roof-leak-repair", "/roof-replacement-phoenix"],
  "roof-maintenance": ["/roof-inspections-for-realtors", "/foam-roof-coating-recoating", "/roof-leak-repair"],
  "roof-inspections-for-realtors": ["/roof-maintenance", "/free-roof-inspection-phoenix", "/roof-leak-repair"],
  "roof-replacement-phoenix": ["/foam-roofing", "/tile-roofing", "/shingle-roofing"],
};

/** Marquee cities surfaced on service pages. */
export const POPULAR_LOCATION_SLUGS = [
  "phoenix-roofing",
  "scottsdale-roofing-contractor",
  "paradise-valley-roofing",
  "mesa-roofing-contractor",
  "chandler-roofing",
  "peoria-roofing-contractor",
];

/** Cost pages surfaced on location pages (so "what does it cost here?" is one click away). */
export const POPULAR_COST_LINKS = [
  "/foam-roof-recoating-cost-in-phoenix",
  "/foam-roof-replacement-cost-phoenix",
  "/tile-roof-replacement-cost-in-phoenix",
  "/roof-repair-cost-phoenix",
];

/** Blog category id → matching service page for inline "need this fixed?" CTA. */
export const CATEGORY_TO_SERVICE: Record<string, { href: string; label: string; tagline: string }> = {
  "foam-roofing": {
    href: "/foam-roof-coating-recoating",
    label: "See our foam roof recoating service",
    tagline: "Got a foam roof? We've been recoating them across the Valley since 1989.",
  },
  "roof-repair": {
    href: "/roof-repair",
    label: "See our roof repair service",
    tagline: "Need a repair? We diagnose the real issue and fix it right the first time.",
  },
  "roof-leaks": {
    href: "/roof-leak-repair",
    label: "See our roof leak repair service",
    tagline: "Active leak? We respond same-day most weeks.",
  },
  "cost-guides": {
    href: "/free-roof-inspection-phoenix",
    label: "Get a free written estimate",
    tagline: "Want real numbers for your roof? Free written inspection — no obligation.",
  },
  "maintenance": {
    href: "/roof-maintenance",
    label: "See our roof maintenance plans",
    tagline: "Catch problems before monsoon season turns them into emergencies.",
  },
  "skylights": {
    href: "/skylight-repair",
    label: "See our skylight repair service",
    tagline: "Skylight leaking? We seal, reflash, or replace.",
  },
  "commercial": {
    href: "/commercial-roofing",
    label: "See our commercial roofing service",
    tagline: "Commercial property? Foam, recoats, and full systems Valley-wide.",
  },
};

/** Pretty label for a path (falls back to titlecased slug). */
export function labelForPath(path: string): string {
  if (PATH_LABELS[path]) return PATH_LABELS[path];
  const slug = path.split("/").filter(Boolean).pop() ?? path;
  return slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function linkItem(href: string, overrides?: Partial<LinkItem>): LinkItem {
  return {
    href,
    title: overrides?.title ?? labelForPath(href),
    blurb: overrides?.blurb ?? PATH_BLURBS[href],
  };
}
